import { cn } from "@/lib/cn";
import type { PisoGrilla } from "@/lib/proyecto";

export interface GrillaPisosProps {
  etiqueta: string;
  /** De arriba hacia abajo (`pisosGrilla` de `lib/proyecto.ts`). */
  pisos: PisoGrilla[];
  elegida: number | null;
  onElegir: (id: number) => void;
}

/** El id de la fila de un piso, para llevar la pantalla hasta ahí (desde la `Fachada`). */
export function idPisoGrilla(piso: number): string {
  return `grilla-piso-${piso}`;
}

/**
 * Las unidades piso por piso (Proyecto): una fila por piso con su nombre y cuántas quedan, y una tarjeta por unidad con
 * número, estado, ambientes, m² y si está al frente. Las que no coinciden con el filtro van apagadas.
 */
export function GrillaPisos({ etiqueta, pisos, elegida, onElegir }: GrillaPisosProps) {
  return (
    <div role="list" aria-label={etiqueta} className="flex flex-col text-carbon">
      {pisos.map((p) => {
        const chicas = p.tarjetas.every((t) => t.chica);
        return (
          <section
            role="listitem"
            key={p.piso}
            id={idPisoGrilla(p.piso)}
            aria-label={p.nombre}
            className="grid scroll-mt-32 grid-cols-[2.75rem_minmax(0,1fr)] gap-3 border-t border-carbon/10 py-5 first:border-t-0 first:pt-0 sm:grid-cols-[4rem_minmax(0,1fr)] sm:gap-4"
          >
            <div className="flex flex-col gap-1 pt-1">
              <span className="text-[1.75rem] font-extralight leading-none tracking-[-0.03em] tabular-nums">{p.etiqueta}</span>
              <span
                className={cn(
                  "text-[0.625rem] font-medium uppercase leading-tight tracking-[0.14em]",
                  p.disponibles > 0 ? "text-[var(--color-estado-disponible)]" : "text-carbon/35",
                )}
              >
                {p.disponibles > 0 ? `${p.disponibles} disp.` : "—"}
              </span>
            </div>
            <div className={cn("grid gap-2.5", chicas ? "grid-cols-[repeat(auto-fill,minmax(6rem,1fr))]" : "grid-cols-2 lg:grid-cols-3")}>
              {p.tarjetas.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  aria-label={`${t.nombre}: ${t.estado}, ${t.detalle}`}
                  aria-pressed={t.id === elegida}
                  onClick={() => onElegir(t.id)}
                  className={cn(
                    "group flex min-w-0 flex-col rounded-tarjeta border border-carbon/10 bg-white text-left transition duration-300",
                    "hover:-translate-y-0.5 hover:border-carbon/25 hover:shadow-flotante focus:outline-none focus-visible:ring-2 focus-visible:ring-marca",
                    t.chica ? "gap-1 px-3 py-2.5" : "gap-2 px-4 py-3.5",
                    t.id === elegida && "border-marca ring-2 ring-marca/60",
                    t.apagada && "opacity-35 hover:opacity-70",
                  )}
                >
                  <span aria-hidden className="flex items-center gap-1.5 text-[0.625rem] font-semibold uppercase tracking-[0.16em]" style={{ color: `color-mix(in srgb, var(${t.token}) 80%, var(--color-carbon))` }}>
                    <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: `var(${t.token})` }} />
                    {t.estado}
                  </span>
                  <span className={cn("truncate font-light tracking-tight", t.chica ? "text-subtitulo" : "text-[1.5rem] leading-tight")}>{t.titulo}</span>
                  <span aria-hidden className={cn("tabular-nums text-carbon/70", t.chica ? "text-[0.75rem]" : "text-chico")}>
                    {t.detalle}
                  </span>
                  {t.ubicacion && (
                    <span aria-hidden className="text-[0.625rem] uppercase tracking-[0.14em] text-carbon/50">
                      {t.ubicacion}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
